/**
 * DetailGoogleMap Component
 * 
 * A React component that renders a Google Map with custom markers and zoom controls.
 * It uses the @react-google-maps/api library to integrate Google Maps into a React application
 * 
 * @author FarmCiti Team
 * @version 1.0.0
 */

import React, { useState, useEffect } from "react";
import {
  useJsApiLoader,
  GoogleMap,
  OverlayViewF,
  OVERLAY_MOUSE_TARGET,
  Circle
} from "@react-google-maps/api";
import { customStyle } from "./style/CustomStyle.js";
import GoogleMapStyle from "../../../assets/stylesheets/map/GoogleMap.module.css";
import ZoomControls from "./ZoomControls.jsx"

export default function DetailGoogleMapComponent({
  markers = [],
  mapContainerStyle = {},
}) {

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY
  });
  const [map, setMap] = useState(null);

  const defaultStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    borderRadius: "12px",    
    overflow: "hidden"
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loadError) return <div>Map failed to load</div>;
  if (!isLoaded) return <div>Loading…</div>;
  
  return (
    <GoogleMap
      key={markers.map((m) => m.id).join(",")}
      mapContainerStyle={{ ...defaultStyle, ...mapContainerStyle }}
      center={markers[0].position}  
      zoom={12}
      onLoad={mapInstance => setMap(mapInstance)}
      options={{
      disableDefaultUI: true,
      zoomControl: false,
      scrollwheel: true,
      gestureHandling: "greedy",
      styles: customStyle
      }}  

    >
      {markers.map((marker) => {
        if (marker.Component) {
          const containerSize = isMobile ? 40 : 50; 

          return (
            <React.Fragment key={marker.id}>
              <Circle
                center={marker.position}
                radius={marker.radius * 6} // meters
                options={{
                  strokeOpacity: 0,
                  fillColor: "rgba(0,0,0,0.15)",
                  fillOpacity: 1,
                  clickable: false
                }}
              />
  
              {/* 2) Logo (OverlayViewF)  */}
              <OverlayViewF
                position={marker.position}
                mapPaneName={OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={() => ({
                  x: -containerSize / 2,
                  y: -containerSize / 2
                })}
              >
                <div
                  className={GoogleMapStyle["map-icon"]}
                  style={{
                    width: `${containerSize}px`,
                    height: `${containerSize}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <marker.Component 
                    style={{
                      width: `${containerSize * 0.7}px`,
                      height: `${containerSize * 0.7}px`
                    }}
                  />
                </div>
              </OverlayViewF>
            </React.Fragment>
          );
        }

      })}

      {/* Zoom Controls */}
      {map && <ZoomControls map={map} />}

    </GoogleMap>
  );
}